/* eslint-disable import/no-extraneous-dependencies */
import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'
import { saveAs } from 'file-saver'
import {
  getCarteiraRanking,
  getCarteiraRankingExport,
  getCarteiraStatus,
  postCarteiraRecalcular,
} from '../../services/CentralResultados/getCentralResultados'

const POLL_INTERVALO_MS = 3000
// Teto de falhas SEGUIDAS do /status antes de desistir (~30s). Falha isolada nao encerra o
// acompanhamento; falha permanente nao pode prender o botao para sempre.
const MAX_FALHAS_POLL = 10

export const useCentralResultados = () => {
  const [ranking, setRanking] = useState<CarteiraRankingType | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  // Vazio = "deixa a API escolher" (ela resolve a ultima competencia JA ENCERRADA, nunca o mes
  // corrente). Nao pre-preencher com o mes atual: isso desfaria a decisao do backend e a tela
  // abriria com todo tenant em R$ 0,00 nos primeiros dias do mes.
  const [competencia, setCompetencia] = useState('')
  const [competenciaComparacao, setCompetenciaComparacao] = useState('')
  const [fases, setFases] = useState<FaseTenant[]>(['EM_FUNCIONAMENTO'])
  const [recalculando, setRecalculando] = useState(false)
  const [isExportando, setIsExportando] = useState(false)

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const montadoRef = useRef(true)
  const falhasSeguidasRef = useRef(0)
  // Guarda de reentrância em ref, e não no estado: a exportação agora é AUDITADA no servidor
  // (`central_exportacao_log`), e uma linha duplicada por duplo-clique é ruído numa trilha que
  // existe para responder "quem levou o quê".
  const exportandoRef = useRef(false)
  // Guarda os filtros correntes para o poll refrescar com o recorte atual, e nao com o que estava
  // na tela quando o recalculo comecou.
  const filtrosRef = useRef({ competencia, competenciaComparacao, fases })
  filtrosRef.current = { competencia, competenciaComparacao, fases }

  useEffect(() => {
    // Reativar na entrada: em StrictMode o efeito roda monta -> limpa -> monta, e sem isto o
    // cleanup deixaria `montadoRef` em false para sempre, fazendo o polling parar de aplicar o
    // resultado (a tela ficaria "recalculando" eternamente).
    montadoRef.current = true
    return () => {
      montadoRef.current = false
      if (pollRef.current) clearTimeout(pollRef.current)
    }
  }, [])

  const fetchRanking = async (source: any) => {
    setIsLoading(true)
    const result = await getCarteiraRanking(
      source,
      competencia,
      competenciaComparacao,
      fases
    )
    if (!montadoRef.current) return
    if ('message' in result) {
      if (result.message !== 'cancelToken') {
        // Erro em estado, e nao so em toast: sem isto a tela fica "Carregando..." para sempre e
        // quem olha nao tem como saber que a chamada falhou.
        setErro(result.message)
        setRanking(null)
        setIsLoading(false)
      }
      return
    }
    setErro(null)
    setRanking(result)
    setRecalculando(result.emAndamento)
    setIsLoading(false)
  }

  useEffect(() => {
    const source = axios.CancelToken.source()
    fetchRanking(source)
    return () => source.cancel('cancelToken')
  }, [JSON.stringify({ competencia, competenciaComparacao, fases })])

  /** Refetch silencioso: usado ao fim do recalculo, sem piscar o loading da tela inteira. */
  const recarregar = async () => {
    const source = axios.CancelToken.source()
    const atual = filtrosRef.current
    const result = await getCarteiraRanking(
      source,
      atual.competencia,
      atual.competenciaComparacao,
      atual.fases
    )
    if (!montadoRef.current) return
    if ('message' in result) {
      if (result.message !== 'cancelToken') toast.error(result.message)
      return
    }
    setErro(null)
    setRanking(result)
    setRecalculando(result.emAndamento)
  }

  const pollStatus = () => {
    pollRef.current = setTimeout(async () => {
      const source = axios.CancelToken.source()
      const result = await getCarteiraStatus(source)
      if (!montadoRef.current) return
      if ('message' in result) {
        // Falha TRANSITORIA do /status nao encerra o acompanhamento: o recalculo continua rodando
        // no servidor, e desistir na primeira falha deixaria a tela dizendo "pronto" com o job no
        // meio. Mas insistir para sempre e o extremo oposto: sessao expirada, 500 permanente ou
        // queda de rede prendiam o botao em "Recalculando..." indefinidamente, batendo no servidor
        // a cada 3s sem nenhum sinal na tela. Teto de tentativas seguidas resolve os dois lados.
        falhasSeguidasRef.current += 1
        if (falhasSeguidasRef.current >= MAX_FALHAS_POLL) {
          setRecalculando(false)
          toast.error(
            'Não foi possível acompanhar o recálculo. Ele pode continuar rodando no servidor — recarregue a página em alguns minutos.'
          )
          return
        }
        pollStatus()
        return
      }
      falhasSeguidasRef.current = 0
      if (result.emAndamento) {
        pollStatus()
        return
      }
      setRecalculando(false)
      recarregar()
    }, POLL_INTERVALO_MS)
  }

  useEffect(() => {
    if (!recalculando) return undefined
    falhasSeguidasRef.current = 0
    pollStatus()
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current)
    }
  }, [recalculando])

  /**
   * Exporta o conjunto INTEIRO do recorte que está na tela.
   *
   * <p>O nome do arquivo usa a competência RESOLVIDA (`ranking?.competencia`), e não o filtro: com o
   * filtro vazio quem escolhe é a API — a última competência congelada pelo rollup, que pode ser
   * mais antiga que o mês passado se o rollup atrasou. Um arquivo chamado "ultimo-mes" contendo
   * junho é pior que um chamado "2026-06".
   *
   * <p>⚠️ Não usa `cancelToken`, seguindo o precedente do PD-0006: desmontar durante a exportação
   * deixa o download terminar, que é o desejado. Se alguém acrescentar o cancelToken depois, precisa
   * filtrar o sentinela `'cancelToken'` aqui — senão vira um toast vermelho escrito "cancelToken" a
   * cada navegação.
   */
  const exportar = async () => {
    if (exportandoRef.current) return
    exportandoRef.current = true
    setIsExportando(true)
    try {
      const result = await getCarteiraRankingExport(
        competencia,
        competenciaComparacao,
        fases
      )
      if (result instanceof Blob) {
        saveAs(
          result,
          `CentralResultados_${ranking?.competencia ?? 'sem-competencia'}.xlsx`
        )
      } else {
        // Acima do teto a API recusa COM orientação — nunca devolve arquivo cortado, que pareceria
        // completo. A mensagem vem do backend e é a ação que a pessoa precisa tomar.
        toast.error(result.message)
      }
    } finally {
      // Sem o finally, uma falha do `saveAs` (createObjectURL bloqueado, browser antigo) deixaria o
      // botão preso em "Gerando…" e desabilitado para sempre, sem saída a não ser recarregar.
      exportandoRef.current = false
      setIsExportando(false)
    }
  }

  const recalcular = async () => {
    if (recalculando) return
    const result = await postCarteiraRecalcular()
    if ('message' in result) {
      toast.error(result.message)
      return
    }
    // 202 (iniciado) e 409 (ja em andamento) sao ambos estado neutro: os dois levam a "recalculando".
    setRecalculando(true)
    toast.info(
      result.iniciado
        ? 'Recálculo iniciado. Os números serão atualizados ao terminar.'
        : 'Já havia um recálculo em andamento. Acompanhando o que está rodando.'
    )
  }

  return {
    ranking,
    isLoading,
    erro,
    competencia,
    competenciaComparacao,
    fases,
    recalculando,
    isExportando,
    exportar,
    onChangeCompetencia: setCompetencia,
    onChangeCompetenciaComparacao: setCompetenciaComparacao,
    // Esvaziar o multiselect faria o param sumir e a API cair no default EM_FUNCIONAMENTO: o filtro
    // pareceria "todas as fases" enquanto o resultado esta restrito a uma. Repor o default deixa a
    // tela dizendo a verdade sobre o que ela esta filtrando.
    onChangeFases: (novas: FaseTenant[]) =>
      setFases(novas.length > 0 ? novas : ['EM_FUNCIONAMENTO']),
    recalcular,
  }
}
